/* eslint-disable max-len */

import { useTranslation } from 'react-i18next';
import {
  capitalizeFirstLetterEveryword
} from '../../../shared/stringFunctions';
import { TXT_ADD_INFILL_ALTERATION, TXT_INFILL_ALTERATIONS } from '../../../translations/translationConstants';
import {
  TXT_SEARCH_BY_NAME
} from '../../../translations/translationConstants';
import { ApolloError, useLazyQuery, useMutation } from '@apollo/client';
import {
  addInfillAlterationInput,
  deleteInfillAlterationInput,
  getInfillAlterationsInput,
  getInfillAlterationOutput,
  GET_INFILL_ALTERATION,
  updateInfillAlterationInput
} from '../../../api';
import { useEffect } from 'react';
import { useState } from 'react';
import { ManageInfillAlterationsTable } from './ManageInfillAlterationsTable';
import {
  DeleteConfirmation,
  ExportToExcel,
  globalPageSizeOptions,
  PaginationBar
} from '../../../components';
import { infillAlteration } from '../../../entityModel';
import { useApolloErrorHandler, useAuthServices } from '../../../hooks';
import {
  ADD_INFILL_ALTERATION,
  DELETE_INFILL_ALTERATION,
  UPDATE_INFILL_ALTERATION
} from '../../../api';
import { ManageInfillAlterationEditor } from './ManagerInfillAlterationEditor';

export const ManageInfillAlterations = () => {
  const { t } = useTranslation();
  const { isSignedIn } = useAuthServices();

  const [txtSearch, setTxtSearch] = useState('');
  const [alterationData, setAlterationData] = useState<infillAlteration[] | undefined>([]);

  const onTxtSearchChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTxtSearch(event.currentTarget.value);
  };

  const [selectedInfillAlteration, setSelectedInfillAlteration] = useState<infillAlteration | undefined>(undefined);
  const [getInfillAlterations, { loading, data, error, refetch }] = useLazyQuery<getInfillAlterationOutput>(GET_INFILL_ALTERATION);
  const [getAllAlterations] = useLazyQuery<getInfillAlterationOutput>(GET_INFILL_ALTERATION, {
    onCompleted: (data) => setAlterationData(data.infillAlterations.items)
  });

  useEffect(() => {
    getInfillAlterations();
    getAllAlterations();
  }, []);

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(globalPageSizeOptions[0]);

  const [openEditor, setOpenEditor] = useState(false);
  const [savingError, setSavingError] = useState<string | undefined>(undefined);

  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);

  const lblSearch = capitalizeFirstLetterEveryword(t(TXT_SEARCH_BY_NAME));

  const fetchInfillAlterations = () => {
    const variables: getInfillAlterationsInput = {
      code: txtSearch,
      take: pageSize,
      skip: (pageNumber - 1) * pageSize
    };
    getInfillAlterations({
      variables
    });
  };

  const fetch = (resetPage?: boolean) => {
    if (!!resetPage && pageNumber !== 1) {
      setPageNumber(1);
    } else {
      fetchInfillAlterations();
    }
  };

  useEffect(() => {
    useApolloErrorHandler(isSignedIn, error);
  }, [error]);

  useEffect(() => {
    fetch();
  }, [pageNumber]);

  useEffect(() => {
    fetch(true);
  }, [pageSize, txtSearch]);

  const openEditInfillAlterationEditor = (infillAlteration: infillAlteration) => {
    setSelectedInfillAlteration(infillAlteration);
    setOpenEditor(true);
  };

  const openDeleteInfillAlterationConfirmation = (infillAlteration: infillAlteration) => {
    setSelectedInfillAlteration(infillAlteration);
    setOpenDeleteConfirmation(true);
  };

  const openAddInfillAlterationEditor = () => {
    setSelectedInfillAlteration(undefined);
    setOpenEditor(true);
  };

  const closeEditor = () => {
    setOpenEditor(false);
  };

  const afterAddingOrUpdatingInfillAlteration = () => {
    setOpenEditor(false);
    if (refetch) {
      refetch();
    }
  };

  const handleAddingOrUpdatingInfillAlterationError = (error: ApolloError) => {
    if (error.graphQLErrors.length > 0 &&
      !!error.graphQLErrors[0].extensions) {
      setSavingError(error.graphQLErrors[0].extensions['message']);
    } else {
      setSavingError(error.message);
    }
  };

  const [addInfillAlteration, addInfillAlterationOperation] = useMutation<infillAlteration>(ADD_INFILL_ALTERATION, {
    onError: (e) => handleAddingOrUpdatingInfillAlterationError(e),
    onCompleted: (result) => afterAddingOrUpdatingInfillAlteration()
  });

  const [updateInfillAlteration, updateInfillAlterationOperation] = useMutation<infillAlteration>(UPDATE_INFILL_ALTERATION, {
    onError: (e) => handleAddingOrUpdatingInfillAlterationError(e),
    onCompleted: (result) => afterAddingOrUpdatingInfillAlteration()
  });

  const onEditorSave = (payload: addInfillAlterationInput | updateInfillAlterationInput) => {
    if (!selectedInfillAlteration) {
      addInfillAlteration({
        variables: {
          input: payload
        }
      });
    } else {
      updateInfillAlteration({
        variables: {
          input: payload
        }
      });
    }
  };

  const onDeleteCancel = () => setOpenDeleteConfirmation(false);

  const [deleteInfillAlteration, deleteInfillAlterationOperation] = useMutation<boolean>(DELETE_INFILL_ALTERATION, {
    onError: (e) => { },
    onCompleted: (result) => {
      if (data && refetch) {
        if (data.infillAlterations.items.length == 1) {
          const targetPage = pageNumber - 1 <= 0 ? 1 : pageNumber - 1;
          if (pageNumber !== targetPage) {
            setPageNumber(targetPage);
          } else {
            refetch();
          }
        } else {
          refetch();
        }
      }
      onDeleteCancel();
    }
  });

  const onDelete = () => {
    const input: deleteInfillAlterationInput = {
      id: selectedInfillAlteration!.id!
    };
    deleteInfillAlteration({
      variables: {
        input
      }
    });
  };

  const onPageSizeChanged = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPageSize(+event.target.value);
  };

  return (
    <div className="content-wrapper">
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1>{t(capitalizeFirstLetterEveryword(TXT_INFILL_ALTERATIONS))}</h1>
            </div>
          </div>
        </div>
      </section>
      <section className="content">
        <div className="container-fluid">
          {
            openEditor &&
            <ManageInfillAlterationEditor
              infillAlteration={selectedInfillAlteration}
              onClose={closeEditor}
              onSave={onEditorSave}
              isSaving={
                !selectedInfillAlteration ? addInfillAlterationOperation.loading :
                  updateInfillAlterationOperation.loading
              }
              savingError={savingError}
            />
          }
          {
            openDeleteConfirmation &&
            <DeleteConfirmation
              loading={deleteInfillAlterationOperation.loading}
              onYes={onDelete}
              onNo={onDeleteCancel}
              objectName={selectedInfillAlteration?.label || ''}
            />
          }
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <div className='row'>
                    <div className='col-6'>
                      <div className="form-group">
                        <label>{lblSearch}</label>
                        <input type='text' className='form-control' value={txtSearch} onChange={onTxtSearchChanged} />
                      </div>
                    </div>
                  </div>
                  <div className='row'>
                    <div className='col-md-6'>
                      <button
                        className='btn btn-primary'
                        data-target="#modal-infillAlteration-editor"
                        data-toggle="modal"
                        disabled={loading}
                        onClick={openAddInfillAlterationEditor}>
                        {t(TXT_ADD_INFILL_ALTERATION).toUpperCase()}
                      </button>
                    </div>
                    <div className="col-md-6" style={{ textAlign: 'right' }}>
                      <ExportToExcel filename={TXT_INFILL_ALTERATIONS} data={alterationData} />
                    </div>
                  </div>
                </div>
                <div className="card-body">
                  <ManageInfillAlterationsTable
                    loading={loading}
                    data={data}
                    error={error}
                    onEdit={openEditInfillAlterationEditor}
                    onDelete={openDeleteInfillAlterationConfirmation}
                  />
                </div>
                <PaginationBar
                  currentPage={pageNumber}
                  totalCount={data?.infillAlterations.totalCount}
                  pageSize={pageSize}
                  onPageChanged={(page) => setPageNumber(page)}
                  onChangeRowsPerPage={onPageSizeChanged}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
